import React, {Dispatch, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';
import {
  CoreActions,
  CoreDoms,
  ViewMode,
  EditorExperienceType,
  CoreEvent,
} from '@taopix/taopix-core';
import {PageInterface} from '@taopix/taopix-core/dist/types/types';
import {PagesSelector} from '../../../Selectors';
import Page from '../../Page/Page';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {ListViewProps} from './ListView';
import {PlusIcon} from '@taopix/taopix-design-system';
import {ActionType} from 'typesafe-actions';
import DeletePageButton from '../../Page/Actions/DeletePageButton';
import {useTranslation} from 'react-i18next';
import AddPagesButton from '../../Page/Actions/AddPagesButton';
import RetroQuantityButton from '../../RetroPrints/RetroQuantityButton';

const RetroListView = ({layout}: ListViewProps) => {
  const ui = useUiState();
  const {t} = useTranslation();
  // TODO: Need to get the retro prints experience
  const perms = {addDeletePage: true};
  const showAddDeleteButtons =
    perms.addDeletePage && layout.minimumprintsperproject !== layout.maximumprintsperproject;

  // get the pages from the store
  const pages = useSelector(PagesSelector);

  // create the action dispatcher for the page quantity
  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();

  const editorType = useMemo(() => {
    return ui.state.canEditPageLayout ? EditorExperienceType.Normal : EditorExperienceType.Locked;
  }, [ui.state.canEditPageLayout]);

  // Classes for the UL
  const pagesContainerClasses = classNames(
    'mt-[70rem]',
    'after:h-[250rem]',
    'after:w-full',
    'after:shrink-0' // Ensure the first/last pages can be centred by the scroll snapping.
  );

  const footerClasses = classNames('flex', 'flex-col', 'min-h-[60rem]');

  const tpxCoreAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const listItemClasses = classNames([
    'flex',
    'flex-col',
    'snap-center',
    !showAddDeleteButtons && 'mb-[40rem]',
  ]);

  const onChangeQty = (truthy: boolean, page: PageInterface) => {
    let value = page.qty === 0 ? 1 : page.qty;
    if (truthy) {
      value += 1;
    } else {
      value -= 1;
    }

    const prevState = page;
    const newState = {
      ...page,
      qty: value,
    };

    dispatch(CoreActions.updatePage(newState));

    const payload = {
      prevState: prevState,
      newState: newState,
      pageNumber: page.pagenumber,
      isRecordingHistory: true,
      fromBrowser: true,
    };

    PubSub.publish(CoreEvent.HISTORY_ADD, payload);
  };

  return (
    <>
      <ul {...tpxCoreAttrs} className={pagesContainerClasses} key={editorType}>
        {pages.map((page: PageInterface) => {
          return (
            <li data-id={page.pagenumber} className={listItemClasses} key={`listview-${page.ref}`}>
              <Page
                page={page}
                layout={layout}
                editorType={editorType}
                viewMode={ViewMode.List}
                showErrors={true}
                showEmptyBoxes={true}
              >
                <div className={footerClasses}>
                  {!ui.state.focusMode && ui.state.pageNumber === page.pagenumber && (
                    <div className={'flex w-full justify-between'}>
                      <RetroQuantityButton page={page} onChangeQty={onChangeQty} />

                      {showAddDeleteButtons && (
                        <DeletePageButton
                          pageNumber={page.pagenumber}
                          buttonProps={{
                            buttonStyle: 'tertiary',
                            hideLabel: true,
                            size: 'small',
                          }}
                          showLabel={false}
                        />
                      )}
                    </div>
                  )}
                </div>
              </Page>
            </li>
          );
        })}
        <li className={'flex flex-col snap-normal'}>
          {showAddDeleteButtons && (
            <AddPagesButton
              buttonProps={{
                buttonStyle: 'tertiary',
                startIcon: <PlusIcon />,
                label: t('*:str_TitleAddMorePrints'),
              }}
              pageNumber={layout.lastpage}
              pageType="print"
            />
          )}
        </li>
      </ul>
    </>
  );
};

export default RetroListView;
